import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { makeStyles, tokens, Text, mergeClasses, Button, Tooltip, Spinner } from '@coreai/fluentui-react';
import { CopyRegular, CheckmarkRegular } from '@fluentui/react-icons';
import { formatTime } from '@/utils/dateTime';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ProposalCard } from './ProposalCard';
import { ProposalFeedback } from './ProposalFeedback';
import { TurnStagesPanel } from './TurnStagesPanel';
import { useAgentChatAguiStore } from '@/stores/agentChatAguiStore';
import type { WorkflowProposal } from '@/types/proposal';
import type { StageEventSnapshot, PipelineStageId, ClarificationSnapshot } from '@/types/agui';
import { ClarificationCard } from './ClarificationCard';
import { ConnectionSetupCard } from './ConnectionSetupCard';
import { CopilotLogoIcon } from '@/components/common/CopilotIcon';
import { getStatusIcon, parseStatusMessage } from './statusIcons';

/**
 * Stable render order for pipeline-stage cards. Used by both the in-flight
 * panel (during streaming) and the persisted-stage panels on completed
 * assistant turns. Kept fixed so the cards don't reorder when stages are
 * re-emitted during the retry loop.
 */
const STAGE_RENDER_ORDER: PipelineStageId[] = [
  'plan',
  'build',
  'validate',
  'retry',
  'ready',
];

/**
 * Sort persisted stages into the same stable {@link STAGE_RENDER_ORDER} used
 * for live stages. Stages whose ids aren't in the canonical order (defensive:
 * older saved chats from a future schema we don't know about) preserve their
 * relative order and append to the end.
 */
function reorderStages(stages: StageEventSnapshot[]): StageEventSnapshot[] {
  const byStage = new Map<PipelineStageId, StageEventSnapshot>();
  const unknown: StageEventSnapshot[] = [];
  for (const s of stages) {
    if (STAGE_RENDER_ORDER.includes(s.stage)) {
      byStage.set(s.stage, s);
    } else {
      unknown.push(s);
    }
  }
  const ordered: StageEventSnapshot[] = [];
  for (const stage of STAGE_RENDER_ORDER) {
    const s = byStage.get(stage);
    if (s) ordered.push(s);
  }
  return [...ordered, ...unknown];
}

const useStyles = makeStyles({
  container: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    gap: tokens.spacingVerticalM,
  },
  emptyIcon: {
    fontSize: '48px',
    color: tokens.colorBrandForeground1,
  },
  // Message row with avatar
  messageRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    maxWidth: '85%',
  },
  userMessageRow: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse',
  },
  aiMessageRow: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
  },
  // Message content wrapper (bubble + timestamp)
  messageContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  userMessageContent: {
    alignItems: 'flex-end',
  },
  aiMessageContent: {
    alignItems: 'flex-start',
  },
  stagesRow: {
    width: '100%',
    maxWidth: '100%',
  },
  stagesContent: {
    width: '100%',
  },
  // Chat bubble
  messageBubble: {
    padding: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    wordBreak: 'break-word',
    fontSize: tokens.fontSizeBase200,
  },
  userBubble: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  aiBubble: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
  },
  // Timestamp
  timestamp: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  // Status line container with subtle pulse animation
  statusLineWrapper: {
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    animationName: {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.7 },
      '100%': { opacity: 1 },
    },
    animationDuration: '2s',
    animationIterationCount: 'infinite',
    animationTimingFunction: tokens.curveEasyEase,
  },
  // Pulsing dot indicator
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
    animationName: {
      '0%': { transform: 'scale(1)', opacity: 1 },
      '50%': { transform: 'scale(1.2)', opacity: 0.7 },
      '100%': { transform: 'scale(1)', opacity: 1 },
    },
    animationDuration: '1.5s',
    animationIterationCount: 'infinite',
    animationTimingFunction: tokens.curveEasyEase,
  },
  // Status line text - no longer nested, part of wrapper
  statusLine: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  statusIcon: {
    display: 'inline-flex',
    fontSize: tokens.fontSizeBase400,
    lineHeight: 1,
    flexShrink: 0,
  },
  successMessage: {
    color: tokens.colorPaletteGreenForeground1,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
  },
  // Markdown content styling
  markdownContent: {
    '& p': {
      margin: 0,
      marginBottom: tokens.spacingVerticalXS,
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& table': {
      borderCollapse: 'collapse',
      margin: `${tokens.spacingVerticalS} 0`,
      fontSize: tokens.fontSizeBase200,
      width: '100%',
    },
    '& th, & td': {
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: tokens.colorNeutralBackground4,
      fontWeight: tokens.fontWeightSemibold,
    },
    '& tr:nth-child(even)': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
    '& ul, & ol': {
      margin: `${tokens.spacingVerticalXS} 0`,
      paddingLeft: tokens.spacingHorizontalL,
    },
    '& li': {
      marginBottom: tokens.spacingVerticalXXS,
    },
    '& code': {
      backgroundColor: tokens.colorNeutralBackground3,
      padding: '0.1em 0.3em',
      borderRadius: '3px',
      fontSize: '0.9em',
    },
    '& pre': {
      backgroundColor: tokens.colorNeutralBackground4,
      padding: tokens.spacingVerticalS,
      borderRadius: tokens.borderRadiusSmall,
      overflowX: 'auto',
      margin: `${tokens.spacingVerticalXS} 0`,
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
      },
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: '0.5em 0 0.25em 0',
      fontWeight: tokens.fontWeightSemibold,
    },
    '& h1': { fontSize: '1.2em' },
    '& h2': { fontSize: '1.1em' },
    '& h3': { fontSize: '1em' },
    '& h4, & h5, & h6': { fontSize: '0.95em' },
    '& blockquote': {
      borderLeft: `3px solid ${tokens.colorNeutralStroke1}`,
      margin: `${tokens.spacingVerticalXS} 0`,
      paddingLeft: tokens.spacingHorizontalM,
      color: tokens.colorNeutralForeground2,
    },
    '& a': {
      color: tokens.colorBrandForegroundLink,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  // User message text with preserved whitespace
  userMessageText: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  proposalContainer: {
    alignSelf: 'stretch',
    width: '100%',
  },
  // Code block with copy button
  codeBlockWrapper: {
    position: 'relative',
    marginBlock: tokens.spacingVerticalXS,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  codeBlockHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: `2px ${tokens.spacingHorizontalS}`,
  },
  codeBlockLanguage: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    fontWeight: tokens.fontWeightMedium,
  },
  copyButton: {
    minWidth: 'auto',
    padding: tokens.spacingVerticalXXS,
    height: '20px',
  },
  copyButtonSuccess: {
    color: tokens.colorPaletteGreenForeground1,
  },
  codeBlockContent: {
    backgroundColor: tokens.colorNeutralBackground1,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    overflowX: 'auto',
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyMonospace,
    whiteSpace: 'pre',
    margin: 0,
    lineHeight: '1.3',
    color: tokens.colorNeutralForeground1,
  },
});

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  stages?: StageEventSnapshot[];
  clarification?: ClarificationSnapshot;
  connectionSetup?: import('@/types/connectionProposal').ConnectionMatchingResult;
  feedback?: import('@/services/workflowAssistantFeedback').FeedbackSentiment;
}

interface StatusListProps {
  chatMessages: ChatMessage[];
  currentStatus: string | null;
  isStreaming: boolean;
  /** True while the initial chat history is being fetched — shows a loader. */
  isLoadingHistory?: boolean;
  /** True once the current turn completed normally (not Stop/error) — shows the feedback control. */
  feedbackEligible?: boolean;
  /** Live streaming text from the builder (accumulated text_message_content). */
  streamingContent?: string;
  /** In-flight stage events for the current assistant turn (keyed by stage id). */
  currentTurnStages?: Partial<Record<StageEventSnapshot['stage'], StageEventSnapshot>>;
  /** When set, the chat is awaiting an answer to the rendered clarification — chat input is disabled. */
  pendingClarification?: ClarificationSnapshot | null;
  /** Called when the user picks an option or sends custom text in the live clarification card. */
  onAnswerClarification?: (text: string, optionId?: string) => void;
}

// Code block component with copy button
// Props type matches react-markdown's CodeComponent expectations
const CodeBlock = (props: React.HTMLAttributes<HTMLElement> & { node?: unknown }) => {
  const { children, className } = props;
  const styles = useStyles();
  const [copied, setCopied] = useState(false);

  // Extract language from className (e.g., "language-json" -> "json")
  const language = className?.replace('language-', '') || '';
  const isCodeBlock = className?.startsWith('language-');

  // Get the text content for copying
  const getTextContent = useCallback((node: React.ReactNode): string => {
    if (typeof node === 'string') return node;
    if (Array.isArray(node)) return node.map(getTextContent).join('');
    if (node && typeof node === 'object' && 'props' in node) {
      return getTextContent((node as React.ReactElement).props.children);
    }
    return '';
  }, []);

  const handleCopy = useCallback(async () => {
    const text = getTextContent(children);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [children, getTextContent]);

  // For inline code, just render as-is
  if (!isCodeBlock) {
    return <code className={className}>{children}</code>;
  }

  // For code blocks, add wrapper with copy button
  return (
    <div className={styles.codeBlockWrapper}>
      <div className={styles.codeBlockHeader}>
        <span className={styles.codeBlockLanguage}>{language || 'code'}</span>
        <Tooltip content={copied ? 'Copied!' : 'Copy code'} relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
            className={mergeClasses(styles.copyButton, copied && styles.copyButtonSuccess)}
            onClick={handleCopy}
          />
        </Tooltip>
      </div>
      <pre className={styles.codeBlockContent}>
        <code>{children}</code>
      </pre>
    </div>
  );
};

export const StatusList = ({ chatMessages, currentStatus, isStreaming, isLoadingHistory, feedbackEligible, streamingContent, currentTurnStages, pendingClarification, onAnswerClarification }: StatusListProps) => {
  const styles = useStyles();
  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  const prevUserMessageCount = useRef(0);
  const [expandedMessageIds, setExpandedMessageIds] = useState<Set<string>>(new Set());

  // In-flight stage events as an ordered list for TurnStagesPanel. We use a
  // STABLE stage order (plan → build → validate → retry → ready)
  // rather than sorting by timestamp, because each stage's timestamp is
  // overwritten on every progress emit — sorting by it would cause cards to
  // reorder repeatedly during the retry loop, which is disorienting.
  const inFlightStages: StageEventSnapshot[] = useMemo(() => {
    if (!currentTurnStages) return [];
    return STAGE_RENDER_ORDER
      .map((stage) => currentTurnStages[stage])
      .filter((s): s is StageEventSnapshot => !!s);
  }, [currentTurnStages]);
  const hasActiveInFlightStage = useMemo(
    () => inFlightStages.some((s) => s.step === 'started' || s.step === 'progress'),
    [inFlightStages],
  );
  const latestInFlightStage = useMemo(
    () =>
      inFlightStages.reduce<StageEventSnapshot | null>(
        (latest, stage) => (!latest || stage.timestamp > latest.timestamp ? stage : latest),
        null,
      ),
    [inFlightStages],
  );
  const shouldShowStreamingFallback = useMemo(
    () =>
      isStreaming &&
      inFlightStages.length > 0 &&
      !hasActiveInFlightStage &&
      latestInFlightStage?.step !== 'failed' &&
      latestInFlightStage?.step !== 'skipped',
    [hasActiveInFlightStage, inFlightStages.length, isStreaming, latestInFlightStage?.step],
  );

  // Memoize the streaming narrative — strip JSON code blocks and STATUS
  // lines so the user sees only the conversational text, not the raw
  // workflow definition or internal status markers.
  const streamingNarrative = useMemo(() => {
    if (!streamingContent) return '';
    return streamingContent
      .replace(/```(?:json)?[\s\S]*?```/g, '')
      .replace(/^STATUS:.*$/gm, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }, [streamingContent]);

  // Toggle timestamp visibility on click (allows multiple messages to show timestamps)
  const handleMessageClick = (messageId: string) => {
    setExpandedMessageIds(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  // Get proposals and actions from store
  const pendingProposals = useAgentChatAguiStore((state) => state.pendingProposals);
  const approveProposal = useAgentChatAguiStore((state) => state.approveProposal);
  const rejectProposal = useAgentChatAguiStore((state) => state.rejectProposal);
  const approveChange = useAgentChatAguiStore((state) => state.approveChange);
  const rejectChange = useAgentChatAguiStore((state) => state.rejectChange);
  const undoChangeDecision = useAgentChatAguiStore((state) => state.undoChangeDecision);
  const applyApprovedChanges = useAgentChatAguiStore((state) => state.applyApprovedChanges);
  // Connection assignment actions
  const approveConnectionAssignment = useAgentChatAguiStore((state) => state.approveConnectionAssignment);
  const rejectConnectionAssignment = useAgentChatAguiStore((state) => state.rejectConnectionAssignment);
  const approveAllConnectionAssignments = useAgentChatAguiStore((state) => state.approveAllConnectionAssignments);
  const skipAllConnectionAssignments = useAgentChatAguiStore((state) => state.skipAllConnectionAssignments);

  // Convert proposals map to array
  const proposalsArray = Array.from(pendingProposals.values());

  // Count user messages
  const userMessageCount = chatMessages.filter(m => m.role === 'user').length;

  // Auto-scroll: when user sends a new message, scroll it to the top of the view
  useEffect(() => {
    if (userMessageCount > prevUserMessageCount.current && lastUserMessageRef.current) {
      // New user message added - scroll it to the top of the container
      lastUserMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevUserMessageCount.current = userMessageCount;
  }, [userMessageCount]);

  // Loading state - fetching the workflow's saved chat history on first open.
  if (isLoadingHistory) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Spinner size="small" label="Loading chat history…" />
        </div>
      </div>
    );
  }

  // Empty state - no activity yet
  if (chatMessages.length === 0 && !isStreaming) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <CopilotLogoIcon className={styles.emptyIcon} />
          <Text size={400} weight="semibold">
            Build with Copilot
          </Text>
          <Text size={200} align="center">
            Describe the process you want to automate, and I'll help build the workflow.
          </Text>
        </div>
      </div>
    );
  }

  const statusParts = currentStatus ? parseStatusMessage(currentStatus) : null;

  // Create a combined timeline of messages and proposals
  type TimelineItem =
    | { type: 'message'; data: ChatMessage }
    | { type: 'proposal'; data: WorkflowProposal };

  const timelineItems: TimelineItem[] = [
    ...chatMessages.map((m) => ({ type: 'message' as const, data: m })),
    ...proposalsArray.map((p) => ({ type: 'proposal' as const, data: p })),
  ].sort((a, b) => a.data.timestamp - b.data.timestamp);

  // Rated replies always show their thumb inline (below). The BOTTOM slot holds
  // the interactive ask for the newest turn: shown while the turn is rateable
  // (feedbackEligible) and hidden once submitted — driven purely by
  // feedbackEligible so a previously-rated reply can't suppress a fresh turn's ask.
  const showBottomInteractive = !isStreaming && feedbackEligible;

  return (
    <div className={styles.container}>
      {/* Combined timeline of messages and proposals */}
      {timelineItems.map((item) => {
        if (item.type === 'proposal') {
          const proposal = item.data;
          return (
            <div key={`proposal-${proposal.id}`} className={styles.proposalContainer}>
              <ProposalCard
                proposal={proposal}
                onApprove={() => approveProposal(proposal.id)}
                onReject={() => rejectProposal(proposal.id)}
                onApproveChange={(changeId) => approveChange(proposal.id, changeId)}
                onRejectChange={(changeId) => rejectChange(proposal.id, changeId)}
                onUndoChange={(changeId) => undoChangeDecision(proposal.id, changeId)}
                onApplyApproved={() => applyApprovedChanges(proposal.id)}
                onApproveConnectionAssignment={(requirementId, referenceKey) =>
                  approveConnectionAssignment(proposal.id, requirementId, referenceKey)
                }
                onRejectConnectionAssignment={(requirementId) =>
                  rejectConnectionAssignment(proposal.id, requirementId)
                }
                onApproveAllConnectionAssignments={() => approveAllConnectionAssignments(proposal.id)}
                onSkipAllConnectionAssignments={() => skipAllConnectionAssignments(proposal.id)}
              />
            </div>
          );
        }

        // Message item
        const message = item.data;
        const isError = message.content.includes('❌');

        // Check if this is the last user message (for scroll targeting)
        const userMessages = timelineItems.filter(i => i.type === 'message' && i.data.role === 'user');
        const isLastUserMessage = message.role === 'user' &&
          userMessages.length > 0 &&
          userMessages[userMessages.length - 1]?.data.id === message.id;

        if (message.role === 'user') {
          return (
            <div
              key={message.id}
              ref={isLastUserMessage ? lastUserMessageRef : undefined}
              className={mergeClasses(styles.messageRow, styles.userMessageRow)}
            >
              {/* Message content */}
              <div className={mergeClasses(styles.messageContent, styles.userMessageContent)}>
                <div
                  className={mergeClasses(styles.messageBubble, styles.userBubble)}
                  onClick={() => handleMessageClick(message.id)}
                >
                  <Text size={200} className={styles.userMessageText}>{message.content}</Text>
                </div>
                {expandedMessageIds.has(message.id) && (
                  <span className={styles.timestamp}>{formatTime(message.timestamp)}</span>
                )}
              </div>
            </div>
          );
        } else {
          const hasStages = !!message.stages && message.stages.length > 0;
          return (
            <div
              key={message.id}
              className={mergeClasses(styles.messageRow, styles.aiMessageRow, hasStages && styles.stagesRow)}
            >
              {/* Message content + persisted pipeline-stage breakdown + clarification card */}
              <div className={mergeClasses(styles.messageContent, styles.aiMessageContent, hasStages && styles.stagesContent)}>
                {message.stages && message.stages.length > 0 && (
                  <TurnStagesPanel stages={reorderStages(message.stages)} live={false} />
                )}
                {message.clarification && (
                  <ClarificationCard
                    snapshot={message.clarification}
                    interactive={pendingClarification?.messageId === message.clarification.messageId}
                    {...(pendingClarification?.messageId === message.clarification.messageId && onAnswerClarification
                      ? { onAnswer: onAnswerClarification }
                      : {})}
                  />
                )}
                {message.content && (
                  <div
                    className={mergeClasses(
                      styles.messageBubble,
                      styles.aiBubble,
                      styles.markdownContent,
                      isError && styles.errorMessage
                    )}
                    onClick={() => handleMessageClick(message.id)}
                  >
                    <Markdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        code: CodeBlock,
                      }}
                    >
                      {message.content}
                    </Markdown>
                  </div>
                )}
                {message.connectionSetup && (
                  <ConnectionSetupCard connectionSetup={message.connectionSetup} />
                )}
                {/* Any rated reply shows its submitted thumb inline and always
                    (independent of streaming/anchor) so it never flickers. The
                    interactive ask lives at the bottom (below). */}
                {message.feedback && (
                  <ProposalFeedback submittedSentiment={message.feedback} />
                )}
                {expandedMessageIds.has(message.id) && (
                  <span className={styles.timestamp}>{formatTime(message.timestamp)}</span>
                )}
              </div>
            </div>
          );
        }
      })}

      {/* In-flight stage cards for the current assistant turn (live) */}
      {isStreaming && inFlightStages.length > 0 && (
        <TurnStagesPanel stages={inFlightStages} live={true} />
      )}
      {/* Keep a visible "still running" indicator when streaming continues
          after the latest stage updates have reached terminal states. */}
      {shouldShowStreamingFallback && (
        <div className={styles.statusLineWrapper} aria-live="polite">
          <span className={styles.statusDot} />
          <div className={styles.statusLine}>
            <Text size={200}>Still working on your workflow…</Text>
          </div>
        </div>
      )}

      {/* Live streaming narrative — memoized to avoid O(n²) regex work on
          every render as streamingContent grows during streaming. */}
      {isStreaming && streamingNarrative && (
        <div className={mergeClasses(styles.messageRow, styles.aiMessageRow)}>
          <div className={mergeClasses(styles.messageContent, styles.aiMessageContent)}>
            <div className={mergeClasses(styles.messageBubble, styles.aiBubble, styles.markdownContent)}>
              <Markdown remarkPlugins={[remarkGfm]} components={{ code: CodeBlock }}>
                {streamingNarrative}
              </Markdown>
            </div>
          </div>
        </div>
      )}

      {/* Legacy single-line status fallback: only show when streaming and no
          structured stage events have been received yet for this turn. */}
      {isStreaming && inFlightStages.length === 0 && statusParts && (
        <div className={styles.statusLineWrapper} aria-live="polite">
          <span className={styles.statusDot} />
          <div className={styles.statusLine}>
            {statusParts.emoji && (
              <span className={styles.statusIcon}>
                {getStatusIcon(statusParts.emoji)}
              </span>
            )}
            <Text size={200}>{statusParts.message}</Text>
          </div>
        </div>
      )}

      {/* Latest reply's interactive feedback ask — rendered at the bottom so
          it's visible after a proposal card. Only shown when the newest turn is
          rateable and not yet rated; once submitted, the thumb shows inline
          under its message (above). */}
      {showBottomInteractive && <ProposalFeedback />}
    </div>
  );
};
